import { Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import { usePaymentSummary } from '@services/dashboard/queries'
import { DashboardStatistics } from '@services/dashboard/queries/useDashboardStatistics'
import dayjs from 'dayjs'
import {
  ArrowDownLeft,
  ArrowUpRight,
  CircleAlert,
  Euro,
  TrendingDown,
  TrendingUp,
  Wallet
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/input'

import { StatTile } from './StatTile'

interface FinanceDashboardProps {
  statistics: DashboardStatistics | undefined
  loading?: boolean
}

const eur = (value?: string | number | null) =>
  `${Number(value ?? 0).toFixed(2)} €`

const FinanceDashboard = ({ statistics, loading }: FinanceDashboardProps) => {
  const { t } = useTranslation('dashboard')
  const [monthParam, setMonthParam] = useState(() =>
    dayjs().add(1, 'month').format('YYYY-MM')
  )
  const { data: paymentSummary, isLoading: paymentSummaryLoading } =
    usePaymentSummary(monthParam)

  const netPositive =
    !paymentSummary || parseFloat(paymentSummary.net_income) >= 0

  return (
    <Section
      title={<SectionTitle icon={<Euro />} title={t('financeDashboard')} />}
      defaultActiveKey={['0']}
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm text-muted-foreground">
            {t('statistics.selectMonth')}:
          </span>
          <Input
            type="month"
            value={monthParam}
            onChange={(e) => e.target.value && setMonthParam(e.target.value)}
            className="w-44"
          />
        </div>

        {paymentSummaryLoading ? (
          <div className="h-20 animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : (
          <div className="grid gap-5 sm:grid-cols-3">
            <StatTile
              icon={netPositive ? TrendingUp : TrendingDown}
              tone={netPositive ? 'green' : 'red'}
              label={t('statistics.netIncome')}
              value={eur(paymentSummary?.net_income)}
            />
            <StatTile
              icon={TrendingDown}
              tone="green"
              label={`${t('statistics.totalIncoming')} (${monthParam})`}
              value={eur(paymentSummary?.total_incoming)}
            />
            <StatTile
              icon={TrendingUp}
              tone="red"
              label={`${t('statistics.totalOutgoing')} (${monthParam})`}
              value={eur(paymentSummary?.total_outgoing)}
            />
          </div>
        )}

        <div className="h-px w-full bg-ink/[0.06]" />

        <div>
          <div className="mb-1 text-base font-semibold text-ink">
            {t('statistics.deposits')}
          </div>
          {/* Deposits are one-off payments with no specific_symbol, so unlike
              the rent figures above they carry no month. They are scoped to
              live rentals instead - see the caption. */}
          <div className="mb-3 text-sm text-muted-foreground">
            {t('statistics.depositsScope')}
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <StatTile
              icon={ArrowDownLeft}
              tone="green"
              label={t('statistics.depositsCollected')}
              value={loading ? '—' : eur(statistics?.deposits_collected_from_clients)}
            />
            <StatTile
              icon={ArrowUpRight}
              tone="amber"
              label={t('statistics.depositsSent')}
              value={loading ? '—' : eur(statistics?.deposits_sent_to_landlords)}
            />
            <StatTile
              icon={Wallet}
              tone="blue"
              label={t('statistics.depositsHeld')}
              value={loading ? '—' : eur(statistics?.deposits_held)}
            />
          </div>
        </div>

        <div className="h-px w-full bg-ink/[0.06]" />

        <div>
          <div className="mb-3 text-base font-semibold text-ink">
            {t('forResolution')}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <StatTile
              icon={CircleAlert}
              tone="red"
              label={t('statistics.unpaidUserRents')}
              value={loading ? '—' : statistics?.unpaid_user_rents_count ?? 0}
            />
            <StatTile
              icon={CircleAlert}
              tone="amber"
              label={t('statistics.unpaidLandlordPayments')}
              value={
                loading ? '—' : statistics?.unpaid_landlord_payments_count ?? 0
              }
            />
          </div>
        </div>
      </div>
    </Section>
  )
}

export default FinanceDashboard
